// SPDX-License-Identifier: AGPL-3.0-or-later

// LiveKit participant attribute the API writes with the video its owner plays on the room TV; clients only read it.
// Value: `yt:<video id>|<started at ms>|<paused at ms or empty>|<written at ms>`, all on the API's epoch clock. The
// shared position is now - started at (paused at - started at while paused). Written at dates the value: against the
// switch coming back on (watch_since) and, received as it changes, for a viewer's clock offset.
export const VOICE_WATCH_ATTRIBUTE = 'micup.watch';

const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com']);
const YOUTUBE_PATH_PREFIXES = ['/shorts/', '/embed/', '/live/'];
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const MAX_URL_LENGTH = 2048;
// https, a bare host (no user info, no port), a path, an optional query and fragment.
const HTTPS_LINK = /^https:\/\/([A-Za-z0-9.-]+)(\/[^?#\s]*)?(?:\?([^#\s]*))?(?:#\S*)?$/;

export interface VoiceWatch {
	videoId: string;
	startedAtMs: number;
	pausedAtMs: number | null;
	writtenAtMs: number;
}

function queryValue(query: string, name: string): string | null {
	for (const pair of query.split('&')) if (pair.startsWith(`${name}=`)) return pair.slice(name.length + 1);
	return null;
}

// The video id of an https YouTube link, or null. Only the id leaves this function: callers build the embed URL.
export function parseYouTubeVideoId(input: string): string | null {
	const text = input.trim();
	if (text.length > MAX_URL_LENGTH) return null;
	const match = HTTPS_LINK.exec(text);
	if (!match) return null;
	const host = match[1]!.toLowerCase();
	const path = match[2] ?? '/';
	let id: string | null = null;
	if (host === 'youtu.be') id = path.slice(1);
	else if (YOUTUBE_HOSTS.has(host)) {
		if (path === '/watch') id = queryValue(match[3] ?? '', 'v');
		else {
			const prefix = YOUTUBE_PATH_PREFIXES.find((p) => path.startsWith(p));
			if (prefix) id = path.slice(prefix.length);
		}
	}
	return id !== null && VIDEO_ID.test(id) ? id : null;
}

export function encodeVoiceWatch(watch: VoiceWatch): string {
	return `yt:${watch.videoId}|${watch.startedAtMs}|${watch.pausedAtMs ?? ''}|${watch.writtenAtMs}`;
}

function epochMs(text: string): number | null {
	if (!/^\d{1,16}$/.test(text)) return null;
	return Number(text);
}

export function decodeVoiceWatch(value: string | undefined): VoiceWatch | null {
	if (!value?.startsWith('yt:')) return null;
	const [videoId, started, paused, written, ...rest] = value.slice(3).split('|');
	if (rest.length > 0 || paused === undefined || written === undefined || !VIDEO_ID.test(videoId ?? '')) return null;
	const startedAtMs = epochMs(started ?? '');
	const pausedAtMs = paused === '' ? null : epochMs(paused);
	const writtenAtMs = epochMs(written);
	if (startedAtMs === null || writtenAtMs === null || (paused !== '' && pausedAtMs === null)) return null;
	return {videoId: videoId!, startedAtMs, pausedAtMs, writtenAtMs};
}

export function voiceWatchPositionMs(watch: VoiceWatch, nowMs: number): number {
	return Math.max(0, (watch.pausedAtMs ?? nowMs) - watch.startedAtMs);
}
